import { ref, watch } from 'vue';
import { useLs } from '@/setup/vueLs';
import { resolvePageSize } from '@/service/pageSize';
import type { UiTableNewFieldBase, UiTableNewSort, UiTableNewFilter } from '@/types/table';

interface TablePaginatorParams {
   lsPath: string; 
   sizes: Record<number, number>;
   defaultSize: number;
}

interface PaginatorData {
  limit?: number | null | undefined;
  page?: number | null | undefined;
}

interface TableSortParams {
  lsPath: string;
  initialOptions?: UiTableNewSort; 
  fields: UiTableNewFieldBase[];
}
interface TableFilterParams {
  lsPath: string;
  initialOptions?: UiTableNewFilter;
}

interface FilterData {
  filters?: any;
}

interface SearchDate {
  search?: string | null;
}


export function useTablePaginator(params: TablePaginatorParams) {
  const ls = useLs();
  const { lsPath, sizes, defaultSize} = params;
  const lsPathLimit = ref('');

  const limit = ref(0);
  const page = ref(1);
  const count = ref(0);

  const pageSize = resolvePageSize(lsPathLimit.value, {
    _default: defaultSize,
    sizes,
  });

  setLsPathPage(lsPath);

  const limitWatchHandle = watch(limit, () => {
    ls.set(lsPathLimit.value, limit.value);
  });

  function setLsPathPage(lsPath: string) {
    lsPathLimit.value = `${lsPath}/limit`;

    limit.value = +ls.get(lsPathLimit.value) || pageSize!;
  }

  const setPage = (data: PaginatorData) => {
    limit.value = Number(data.limit) || pageSize!;
    page.value = Number(data.page) || 1;
  };

  return { limitWatchHandle, limit, page, count, setPage, setLsPathPage };
}

export function useTableSort(params: TableSortParams) {
  const ls = useLs();
  const { lsPath, initialOptions, fields} = params;
  const lsPathColumn = ref('');
  const lsPathDir = ref('');

  const initialColumn = initialOptions?.sort_column || 'name';
  const initialSort = initialOptions?.sort_dir || 'asc';

  const sortProp = ref('');
  const sortOrder = ref('');
  const optionsSort = ref({
    sort_column: '',
    sort_dir: '',
  });

  setLsPath(lsPath);

  const sortPropWatchHandle = watch(sortProp, (newValue) => {
    optionsSort.value.sort_column = sortProp.value;
    if (
      fields.some(
        (item) => item.name === optionsSort.value.sort_column && item.sortable === 'custom',
      )
    ) {
      ls.set(lsPathColumn.value, newValue || initialColumn);
    }
  }, {immediate: true});

  const sortOrderWatchHandle = watch(sortOrder, (newValue) => {
    optionsSort.value.sort_dir = sortOrder.value;
    ls.set(lsPathDir.value, newValue || initialSort);
  }, {immediate: true});

  const setSort = (data: UiTableNewSort) => {
    sortProp.value = data.sort_column || initialColumn;
    sortOrder.value = data.sort_dir || initialSort;
  };

  function setLsPath(lsPath: string) {
    lsPathColumn.value = `${lsPath}/sort_column`;
    lsPathDir.value = `${lsPath}/sort_dir`;

    sortProp.value = ls.get(lsPathColumn.value) ?? initialColumn;
    sortOrder.value = ls.get(lsPathDir.value) ?? initialSort;
  }

  return { sortProp, sortOrder, setSort, sortPropWatchHandle, sortOrderWatchHandle, optionsSort, setLsPath };
}

export function useTableSearch() {
  const searchQuery = ref('');

  const setSearch = (data: SearchDate) => {
    searchQuery.value = data.search ?? '';
  };
  return { searchQuery, setSearch };
}

export function useTableFilter(params: TableFilterParams) {
  const ls = useLs();
  const { lsPath, initialOptions } = params;
  const lsPathFilter = `${lsPath}/filters`;

  const initialFilter = initialOptions?.filter || {};

  const filterParams = ref<UiTableNewFilter['filter']>({});
  filterParams.value = {...(ls.get(lsPathFilter) ?? initialFilter)};

  const filterWatchHandle = watch(filterParams, (newValue) => {
    ls.set(lsPathFilter, newValue || initialFilter);
  }, {immediate: true, deep: true});

  const setFilter = (data: FilterData, defaultFilter = {}, parse = true) => {
    let filter = defaultFilter;
    if(data.filters) {
      filter = parse ? JSON.parse(data.filters) : data.filters;
    }
    filterParams.value = { ...filter };
  };

  return { filterParams, setFilter, filterWatchHandle };
}
